import React from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import { addDays, differenceInCalendarDays, format, parseISO, startOfWeek } from 'date-fns';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';

/** Strength of the theme's primary colour per step: empty, then four levels of busy. */
const STEPS = [0, 0.25, 0.5, 0.75, 1];
const WEEKDAYS = [
    { day: 'mon', letter: 'M' },
    { day: 'tue', letter: 'T' },
    { day: 'wed', letter: 'W' },
    { day: 'thu', letter: 'T' },
    { day: 'fri', letter: 'F' },
    { day: 'sat', letter: 'S' },
    { day: 'sun', letter: 'S' },
];

const step = (count: number, max: number) =>
    count === 0 ? 0 : Math.min(4, Math.ceil((count / Math.max(1, max)) * 4));

/**
 * The last 12 weeks, sized to the side column: a row per weekday (Monday first) and a column per
 * week, so the same weekday lines up across weeks. Darker is busier, relative to the user's own
 * busiest day. Colours come from the theme, so it reads in dark mode too.
 */
const ActivityGrid = ({ grid }: { grid: { date: string; count: number }[] }) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const { formatDateString } = useDateUtils();

    if (grid.length === 0) return null;
    const firstDate = parseISO(grid[0].date);
    const lastDate = parseISO(grid[grid.length - 1].date);
    // The API sends YYYY-MM-DD; anything else would make date-fns throw and take the page down.
    if (Number.isNaN(firstDate.getTime()) || Number.isNaN(lastDate.getTime())) return null;

    const max = Math.max(0, ...grid.map(cell => cell.count));
    const fill = (level: number) =>
        level === 0 ? theme.palette.action.hover : alpha(theme.palette.primary.main, STEPS[level]);
    const first = firstDate;
    const weekStart = startOfWeek(first, { weekStartsOn: 1 });
    const weeks = Math.ceil((differenceInCalendarDays(lastDate, weekStart) + 1) / 7);
    const counts = new Map(grid.map(cell => [cell.date, cell.count]));
    const active = grid.filter(cell => cell.count > 0).length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
                {t('Modules.Admin.Usage.activeDaysOfGrid', { active, total: grid.length })}
            </Typography>
            <Box
                role="img"
                aria-label={t('Modules.Admin.Usage.activeDaysOfGrid', {
                    active,
                    total: grid.length,
                })}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `14px repeat(${weeks}, minmax(0, 1fr))`,
                    gap: '3px',
                    alignItems: 'center',
                }}
            >
                {WEEKDAYS.map(({ day, letter }, row) => (
                    <React.Fragment key={day}>
                        <Typography
                            component="span"
                            sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1 }}
                        >
                            {letter}
                        </Typography>
                        {Array.from({ length: weeks }, (_, week) => {
                            const date = format(addDays(weekStart, week * 7 + row), 'yyyy-MM-dd');
                            const count = counts.get(date);
                            return (
                                <Box
                                    key={date}
                                    title={
                                        count === undefined
                                            ? undefined
                                            : t('Modules.Admin.Usage.activityGridLabel', {
                                                  date: formatDateString({
                                                      date,
                                                      removeTime: true,
                                                  }),
                                                  count,
                                              })
                                    }
                                    sx={{
                                        aspectRatio: '1',
                                        borderRadius: '2px',
                                        bgcolor:
                                            count === undefined
                                                ? 'transparent'
                                                : fill(step(count, max)),
                                    }}
                                />
                            );
                        })}
                    </React.Fragment>
                ))}
            </Box>
            <Box
                aria-hidden="true"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Typography variant="caption" color="text.disabled">
                    {format(first, 'd MMM')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Typography variant="caption" color="text.disabled">
                        {t('Modules.Admin.Usage.less')}
                    </Typography>
                    {STEPS.map((_, level) => (
                        <Box
                            // eslint-disable-next-line react/no-array-index-key -- a fixed five-step key
                            key={level}
                            sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: fill(level) }}
                        />
                    ))}
                    <Typography variant="caption" color="text.disabled">
                        {t('Modules.Admin.Usage.more')}
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.disabled">
                    {t('Modules.Admin.Usage.today')}
                </Typography>
            </Box>
        </Box>
    );
};

export default ActivityGrid;
